import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { ListItem } from "@material-library";
import { UserService } from "src/app/core/user.service";
import * as fromRoot from '../../reducers';
import { BlendEvent, PermissionActions, User, UserInfoSetApiOutput } from "src/app/shared/models";
import { MatSelectChange } from "@angular/material/select";
import { ApiService } from "src/app/core/api.service";
import { SnackbarService } from "src/app/shared/services/snackbar.service";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { BlendService } from "src/app/shared/services/blend.service";
import { snackBarData } from "src/app/shared/models/snackbar-data.model";
import { Subscription } from "rxjs";

@Component({
  selector: "app-security",
  templateUrl: "./security.component.html",
  styleUrls: ["./security.component.scss"],
})
export class SecurityComponent implements OnInit, OnDestroy {
  public securityFeatures: ListItem[] = [];
  public user: User;
  public isPassResetHidden: boolean;
  public permissionActions = PermissionActions;
  public is2FaEditable: boolean = true;
  public showTwoFactorDialog: boolean = false;
  public showPasswordHintDialog: boolean = false;
  public showPasswordRecoveryDialog: boolean = false;
  private userSubscription: Subscription;

  constructor(
    private store: Store<fromRoot.State>,
    private userService: UserService,
    private apiService: ApiService,
    private snackbarService: SnackbarService,
    private blendService: BlendService
  ) { }

  ngOnInit(): void {
    this.userSubscription = this.store
      .select(fromRoot.getAuthState)
      .subscribe((data) => {
        if (!data.authenticated) {
          console.warn('User is not authenicated');
          this.user = new User();
          // this.initialized = false;
          return;
        }
        if (data.user) {
          this.user = data.user;
          this.isPassResetHidden = this.user.is_multi_new ? this.user.is_multi_new : (this.user.is_multi_child && this.user.is_resetpassword == 1);
          this.initSecurityFeatures();
        }
        if (this.user.is_multi_new) {
          this.is2FaEditable = !this.userService.getPermssionByKey(PermissionActions.FORCE_2FA_ON_FIRST_LOGIN);
        }
      });
    this.initSecurityFeatures();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  initSecurityFeatures() {
    this.securityFeatures = [
      {
        primaryText: "Two-factor authentication",
        secondaryText: "Two-factor authentication is a recommended security feature that can help protect your Sync account from unauthorized access.",
        divider: true,
        trail1AdornmentConfig: {
          enabled: true,
          type: "chip",
          size: "large",
          chipConfig: {
            label: (this.user.twofa_type != '0') ? 'Enabled' : 'Disabled',
            size: "large",
            readOnly: true,
            leadAdornment: {
              enabled: true,
              type: "icon",
              iconName: (this.user.twofa_type != '0') ? 'success' : 'error',
              size: "large",
            },
          },
        },
        trail2AdornmentConfig: {
          enabled: this.is2FaEditable ? true : false,
          type: 'iconButton',
          size: 'large',
          iconButtonConfig:
          {
            iconName: 'settings',
            size: 'large'
          },
          onClick: () => this.showTwoFactorDialog = true,
        },
      },
      {
        primaryText: "Password hint",
        secondaryText: "Set a word or phrase that can help you remember a forgotten password.",
        divider: true,
        trail1AdornmentConfig: {
          enabled: true,
          type: "chip",
          size: "large",
          chipConfig: {
            label: (this.user.has_password_hint != 0) ? 'Enabled' : 'Disabled',
            size: "large",
            readOnly: true,
            leadAdornment: {
              enabled: true,
              type: "icon",
              iconName: (this.user.has_password_hint != 0) ? 'success' : 'error',
              size: "large",
            },
          },
        },
        trail2AdornmentConfig: {
          enabled: true,
          type: 'iconButton',
          size: 'large',
          iconButtonConfig:
          {
            iconName: 'settings',
            size: 'large'
          },
          onClick: () => this.showPasswordHintDialog = true,
        },
      },
      {
        primaryText: "Email-based password recovery",
        secondaryText: "Make your Sync account recoverable via email authentication.",
        divider: true,
        trail1AdornmentConfig: {
          enabled: true,
          type: "chip",
          size: "large",
          chipConfig: {
            label: (this.user.is_resetpassword != 0) ? 'Enabled' : 'Disabled',
            size: "large",
            readOnly: true,
            leadAdornment: {
              enabled: true,
              type: "icon",
              iconName: (this.user.is_resetpassword != 0) ? 'success' : 'error',
              size: "large",
            },
          },
        },
        trail2AdornmentConfig: {
          enabled: true,
          type: 'iconButton',
          size: 'large',
          iconButtonConfig:
          {
            iconName: 'settings',
            size: 'large'
          },
          onClick: () => this.showPasswordRecoveryDialog = true,
        },
      },
      {
        primaryText: 'Automatic logout',
        secondaryText: 'Automatic logout allows you to configure session expiry time for all web browser sessions.',
        divider: true,
        trail2AdornmentConfig: {
          enabled: true,
          type: 'select',
          size: 'large',
          selectConfig: {
            list: [
              {
                value: '0',
                name: 'Never',
              },
              {
                value: '3600',
                name: 'After 1 hour',
              },
              {
                value: '86400',
                name: 'After 24 hours',
              }
            ],
            label: 'Auto logout',
            size: 'small',
            value: this.user.sessionttl.toString(),
            onSelectionChange: this.autoLogout
          },
        },
      },
      {
        primaryText: 'Single Sign-On for desktop application',
        secondaryText: 'Single sign-on (SSO) allows you to log in directly to the web panel from an installed and authenticated desktop app attached to your account.',
        divider: true,
        trail2AdornmentConfig: {
          enabled: true,
          type: 'switch',
          size: 'large',
          switchConfig: {
            size: 'large',
            checked: this.user.is_disablesso === 1 ? false : true,
          },
          onChange: this.setSSOStatus
        }
      },
      {
        primaryText: 'Analytics tracking',
        secondaryText: 'Analytics tracking allows us to understand how users interact with our application, helping us improve and personalize your experience while respecting your privacy.',
        trail2AdornmentConfig: {
          enabled: true,
          type: 'switch',
          size: 'large',
          switchConfig: {
            size: 'large',
            checked: this.user.is_disableanalytics === 1 ? false : true,
          },
          onChange: this.setAnalytics
        }
      },
    ];

    // If password reset feature is hidden, remove it from the security features array.
    if (this.isPassResetHidden) {
      // Find the index of the emailPasswordRecovery object in the securityFeatures array
      const indexToRemove = this.securityFeatures.findIndex(feature => feature.primaryText === "Email-based password recovery");

      // If the object exists in the array, remove it
      if (indexToRemove !== -1) {
        this.securityFeatures.splice(indexToRemove, 1);
      }
    }
  }

  autoLogout = async (autologoutTtl: MatSelectChange) => {
    try {
      await this.apiService.execute<UserInfoSetApiOutput>('userinfoset', {
        sessionttl: parseInt(autologoutTtl.value, 10)
      });
      this.userService.refresh();
      this.snackbarService.showMessage("success", "Automatic logout time updated.");
    } catch (ex) {
      this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
      this.initSecurityFeatures();
    }
  }

  setStatusMessage = (status: snackBarData["type"], isEnabled: boolean, successMessage: string) => {
    const statusMessage = isEnabled ? 'enabled.' : 'disabled.';
    this.snackbarService.showMessage(status, successMessage + statusMessage);
  };

  setSSOStatus = async (ssoStatusChange: MatSlideToggleChange) => {
    const ssoStatus = ssoStatusChange.checked === true ? '0' : '1';
    try {
      await this.apiService.execute<UserInfoSetApiOutput>('userinfoset', {
        disablesso: ssoStatus
      });
      this.userService.refresh();
      this.setStatusMessage('success', ssoStatus === '0', 'Single sign-on ');
    } catch (ex) {
      this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
      this.initSecurityFeatures();
    }
  }

  setAnalytics = async (analyticsStatusChange: MatSlideToggleChange) => {
    const analyticsStatus = analyticsStatusChange.checked === true ? '0' : '1';
    try {
      await this.apiService.execute<UserInfoSetApiOutput>('userinfoset', {
        disableanalytics: analyticsStatus,
      });
      const event_name = analyticsStatus === '0' ? BlendEvent.ANALYTICS_OPT_IN : BlendEvent.ANALYTICS_OPT_OUT;
      this.blendService.track(event_name, {});
      this.userService.refresh();
      this.setStatusMessage('success', analyticsStatus === '0', 'Analytics tracking ');
    } catch (ex) {
      this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
      this.initSecurityFeatures();
    }
  }
}
